<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch, inject } from "vue"
import { useI18n } from "vue-i18n"
import moment from "moment"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useAuthStore } from "@core/stores/auth.store"
import { useConfigStore } from "@core/stores/config.store"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"
import { useTaskStore } from "@core/stores/task.store"
import type { Services } from "@services"

// ================================
// PROPS & EMITS
// ================================

const visible = defineModel<boolean>("visible", {
    required: true,
    default: false,
})

// ================================
// INTERNATIONALIZATION
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            title: "Export des données du bâtiment",
            question: "Spécifiez la période, les variables et les fréquences.",
            period: {
                label: "Période",
                start_date: "Date de début",
                end_date: "Date de fin",
                error: "La date de début doit être antérieure à la date de fin.",
            },
            variables: {
                label: "Variables à exporter",
                no_variables:
                    "Aucune variable disponible pour ce bâtiment sur la période.",
                loading_variables: "Chargement des variables...",
                hint: "Sélectionnez une ou plusieurs variables",
                error: "Sélectionnez au moins une variable.",
                elec: "Électricité",
                gaz: "Gaz",
                weather_station: "Station météo",
                other: "Autres",
            },
            freqs: {
                label: "Fréquences",
            },
            submit: "Générer l'export",
            loading: "Génération en cours...",
            no_building: "Aucun bâtiment sélectionné.",
            task_export_title: "Export des données",
            task_export_success: "L'export est prêt. Le fichier va s'ouvrir.",
            task_export_failure: "L'export a échoué.",
        },
    },
})

// ================================
// STORES & SERVICES
// ================================

const buildingStore = useBuildingStore()
const authStore = useAuthStore()
const configStore = useConfigStore()
const taskStore = useTaskStore()
const services = inject<Services>("services")

// ================================
// STATE
// ================================

const startDate = ref<Date>(moment().subtract(3, "month").toDate())
const endDate = ref<Date>(moment().toDate())
const variablesSelected = ref<string[]>([])
const freqsSelected = ref<string[]>(["hourly", "daily", "weekly", "monthly"])
const errorMessage = ref("")

// ================================
// COMPUTED
// ================================

/**
 * Types de variables disponibles pour le bâtiment : agrégation de getSourceVariables pour chaque source.
 */
const getVariableList = computed(() => {
    const sources = buildingStore.getSources
    const list: string[] = []
    for (const source of sources) {
        const vars = buildingStore.getSourceVariables(source._id_source)
        for (const v of vars) {
            if (!list.includes(v)) list.push(v)
        }
    }
    return list
})

/** Indique si on a besoin de la config variables (liste de types non vide). */
const getNeedVariablesConfig = computed(() => getVariableList.value.length > 0)

/**
 * Indique si les variables de config sont en cours de chargement.
 * Vrai quand on en a besoin, que la config est vide et que le fetch est en cours.
 */
const getIsVariablesConfigLoading = computed(
    () =>
        getNeedVariablesConfig.value &&
        configStore.getVariables.length === 0 &&
        configStore.isLoadingVariables
)

/**
 * Liste des variables au format "type.stat" en faisant correspondre chaque type
 * avec la variable de configuration (config.store) pour récupérer le stat associé.
 * Si la variable n'est pas trouvée en config, fallback sur "type.sum".
 */
const getVariableWithStatList = computed(() => {
    return getVariableList.value.map((type) => {
        const configVariable = configStore.getVariableByType(type)
        const stat = configVariable?.stat ?? "sum"
        return `${type}.${stat}`
    })
})

/**
 * Retourne la catégorie d'une variable (type) pour le regroupement, comme dans le legacy.
 * elec | gaz | weather_station | other
 */
function getVariableCategory(
    type: string
): "elec" | "gaz" | "weather_station" | "other" {
    if (type?.includes("elec")) return "elec"
    if (type?.includes("gaz")) return "gaz"
    if (type?.includes("exterieur")) return "weather_station"
    return "other"
}

/**
 * Options pour le multi-select : label = nom config ou type, value = "type.stat".
 * C'est la valeur "type.stat" qui est envoyée à l'API.
 */
const getVariableOptions = computed(() =>
    getVariableList.value.map((type) => {
        const configVariable = configStore.getVariableByType(type)
        const stat = configVariable?.stat ?? "sum"
        const value = `${type}.${stat}`
        const label = configVariable?.name ?? type
        return { label, value, type }
    })
)

/** Ordre d'affichage des catégories (comme dans le legacy). */
const VARIABLE_CATEGORY_ORDER: (
    | "elec"
    | "gaz"
    | "weather_station"
    | "other"
)[] = ["elec", "gaz", "weather_station", "other"]

/**
 * Options des variables regroupées par catégorie (Électricité, Gaz, Station météo, Autres).
 */
const getVariableOptionsGrouped = computed(() => {
    const options = getVariableOptions.value
    const byCategory = new Map<
        "elec" | "gaz" | "weather_station" | "other",
        { label: string; value: string }[]
    >()
    for (const opt of options) {
        const category = getVariableCategory(opt.type)
        const list = byCategory.get(category) ?? []
        list.push({ label: opt.label, value: opt.value })
        byCategory.set(category, list)
    }
    return VARIABLE_CATEGORY_ORDER.filter(
        (cat) => (byCategory.get(cat)?.length ?? 0) > 0
    ).map((category) => ({
        label: t(`variables.${category}`),
        items: byCategory.get(category) ?? [],
    }))
})

/** Options des fréquences (building export : hourly, daily, weekly, monthly). */
const getFreqOptions = computed(() => [
    { value: "hourly", label: "Horaire" },
    { value: "daily", label: "Journalier" },
    { value: "weekly", label: "Hebdomadaire" },
    { value: "monthly", label: "Mensuel" },
])

/** Validation des dates (début <= fin). */
const getIsValidDates = computed(() =>
    moment(startDate.value).isSameOrBefore(moment(endDate.value))
)

/** Formulaire valide. */
const getIsFormValid = computed(() => {
    if (!buildingStore.building?._id) return false
    if (!getIsValidDates.value) return false
    if (getIsVariablesConfigLoading.value) return false
    return variablesSelected.value.length > 0
})

// ================================
// FETCH EXPORT
// ================================

const { fetch: fetchExportBuilding, isFetching: isExporting } = useFetchWrapper(
    async (_params, controller) => {
        const _id_building = buildingStore.building?._id
        const _id_user = authStore.userId ?? ""
        if (!_id_building || !services?.$getExportBuilding) return
        const outcome = await services.$getExportBuilding(
            {
                _id_building,
                date_from: moment(startDate.value).format("YYYY-MM-DD"),
                date_to: moment(endDate.value).format("YYYY-MM-DD"),
                variables: variablesSelected.value,
                freqs: freqsSelected.value,
                _id_user,
            },
            controller
        )
        if (outcome.mode === "sync") {
            window.open(outcome.fileUrl, "_blank")
            visible.value = false
            return
        }
        taskStore.followTask(outcome.taskId, {
            taskname: "task_route_async",
            taskToastTitle: t("task_export_title"),
            taskToastSuccessMessage: t("task_export_success"),
            taskToastFailureMessage: t("task_export_failure"),
            notification: true,
            autoDelete: true,
        })
        visible.value = false
    },
    (err) => {
        errorMessage.value =
            err instanceof Error ? err.message : "Erreur lors de l'export"
    }
)

// ================================
// WATCHERS
// ================================

/** Réinitialise les variables sélectionnées (format "type.stat") quand la liste change. */
watch(
    () => getVariableWithStatList.value,
    (variableWithStatList) => {
        variablesSelected.value =
            variableWithStatList.length > 0 ? [...variableWithStatList] : []
    },
    { immediate: true }
)

/** Quand le dialog s'ouvre et qu'on a des types de variables mais pas de config, on charge les variables. */
watch(
    () =>
        [
            visible.value,
            getVariableList.value.length,
            configStore.getVariables.length,
        ] as const,
    ([vis, typesLength, configLength]) => {
        if (vis && typesLength > 0 && configLength === 0) {
            configStore.fetchVariables()
        }
    },
    { immediate: true }
)

// ================================
// INTERACTIONS
// ================================

async function onSubmitForm() {
    if (!getIsFormValid.value || !services) return
    errorMessage.value = ""
    await fetchExportBuilding()
}

function onOpen() {
    startDate.value = moment().subtract(3, "month").toDate()
    endDate.value = moment().toDate()
    freqsSelected.value = ["hourly", "daily", "weekly", "monthly"]
    errorMessage.value = ""
}
</script>

<template>
    <BaseDialog
        v-model:visible="visible"
        modal
        closable
        dismissable-mask
        position="top"
        class="export-building-dialog max-w-[32rem] w-full"
        :header="t('title')"
        :style="{ width: '100%' }"
        :breakpoints="{ '575px': '90vw' }"
        @show="onOpen"
    >
        <p v-if="!buildingStore.building" class="text-surface-600 text-sm mb-4">
            {{ t("no_building") }}
        </p>
        <template v-else>
            <p class="text-surface-600 text-sm mb-4">
                {{ t("question") }}
            </p>

            <form
                class="export-building-dialog__form flex flex-col gap-5"
                @submit.prevent="onSubmitForm"
            >
                <!-- Période -->
                <div class="flex flex-col gap-2">
                    <label class="text-sm font-medium text-surface-700">
                        {{ t("period.label") }}
                    </label>
                    <div class="flex flex-wrap gap-4">
                        <BaseInputWrapper
                            input-id="export-building-date-from"
                            :label="t('period.start_date')"
                            :error="
                                !getIsValidDates ? t('period.error') : undefined
                            "
                        >
                            <BaseDatePicker
                                id="export-building-date-from"
                                v-model="startDate"
                                date-format="dd/mm/yy"
                                :max-date="endDate"
                                :invalid="!getIsValidDates"
                                class="w-full"
                            />
                        </BaseInputWrapper>
                        <BaseInputWrapper
                            input-id="export-building-date-to"
                            :label="t('period.end_date')"
                            :error="
                                !getIsValidDates ? t('period.error') : undefined
                            "
                        >
                            <BaseDatePicker
                                id="export-building-date-to"
                                v-model="endDate"
                                date-format="dd/mm/yy"
                                :invalid="!getIsValidDates"
                                class="w-full"
                            />
                        </BaseInputWrapper>
                    </div>
                </div>

                <!-- Variables -->
                <div class="flex flex-col gap-2">
                    <label class="text-sm font-medium text-surface-700">
                        {{ t("variables.label") }}
                    </label>
                    <div
                        v-if="getIsVariablesConfigLoading"
                        class="flex items-center gap-2 text-surface-500 text-sm py-2"
                    >
                        <i class="pi pi-spin pi-spinner" aria-hidden="true" />
                        <span>{{ t("variables.loading_variables") }}</span>
                    </div>
                    <div
                        v-else-if="getVariableList.length === 0"
                        class="text-warn text-sm"
                    >
                        {{ t("variables.no_variables") }}
                    </div>
                    <BaseMultiSelect
                        v-else
                        v-model="variablesSelected"
                        :options="getVariableOptionsGrouped"
                        option-label="label"
                        option-value="value"
                        option-group-label="label"
                        option-group-children="items"
                        :placeholder="t('variables.hint')"
                        filter
                        :disabled="isExporting"
                        class="w-full"
                    />
                </div>

                <!-- Fréquences -->
                <div class="flex flex-col gap-2">
                    <label class="text-sm font-medium text-surface-700">
                        {{ t("freqs.label") }}
                    </label>
                    <BaseMultiSelect
                        v-model="freqsSelected"
                        :options="getFreqOptions"
                        option-label="label"
                        option-value="value"
                        :disabled="isExporting"
                        class="w-full"
                    />
                </div>

                <BaseMessage v-if="errorMessage" severity="error" class="mt-2">
                    {{ errorMessage }}
                </BaseMessage>

                <BaseButton
                    type="submit"
                    :label="isExporting ? t('loading') : t('submit')"
                    :disabled="!getIsFormValid || isExporting"
                    :loading="isExporting"
                    class="w-full mt-2"
                />
            </form>
        </template>
    </BaseDialog>
</template>
